import { useState } from "react"
import { Box, Button, Link, Stack, Typography } from "@mui/material"
import type { PortfolioProject, PortfolioShot } from "./projects"
import { Lightbox } from "./Lightbox"

type Props = {
  project: PortfolioProject
}

function ShotFrame({
  shot,
  onOpen,
}: {
  shot: PortfolioShot
  onOpen: () => void
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`Open ${shot.caption}`}
      sx={{
        display: "block",
        width: "100%",
        p: 0,
        border: "1px solid rgba(140,190,255,0.28)",
        bgcolor: "rgba(0,0,0,0.45)",
        cursor: "zoom-in",
        textAlign: "left",
        overflow: "hidden",
        "&:hover img": { opacity: 0.92 },
      }}
    >
      <Box
        component="img"
        src={shot.src}
        alt={shot.alt}
        loading="lazy"
        sx={{
          width: "100%",
          height: "auto",
          display: "block",
          aspectRatio: "16 / 10",
          objectFit: "cover",
          objectPosition: "top center",
        }}
      />
      <Typography
        sx={{
          px: 1.25,
          py: 0.85,
          fontSize: "0.62rem",
          letterSpacing: "0.16em",
          color: "rgba(170,200,235,0.75)",
        }}
      >
        {shot.caption}
      </Typography>
    </Box>
  )
}

export function ProjectSection({ project }: Props) {
  const all = [project.primary, ...project.gallery]
  const [lightbox, setLightbox] = useState<number | null>(null)

  return (
    <Box
      id={project.anchor}
      component="section"
      sx={{
        position: "relative",
        zIndex: 2,
        scrollMarginTop: 24,
        px: { xs: 2, md: 4 },
        py: { xs: 8, md: 12 },
        maxWidth: 1100,
        mx: "auto",
        pointerEvents: "auto",
      }}
    >
      <Typography
        sx={{
          letterSpacing: "0.24em",
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "rgba(120,230,255,0.85)",
          mb: 1,
        }}
      >
        {project.eyebrow}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: { xs: "2rem", md: "3rem" },
          lineHeight: 1.05,
          mb: 1.5,
        }}
      >
        {project.title}
      </Typography>
      <Typography
        sx={{
          maxWidth: 640,
          color: "rgba(210,225,255,0.8)",
          fontSize: { xs: "0.98rem", md: "1.05rem" },
          lineHeight: 1.55,
          mb: 3,
        }}
      >
        {project.description}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <ShotFrame shot={project.primary} onOpen={() => setLightbox(0)} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 1.5,
          mb: 3,
        }}
      >
        {project.gallery.map((shot, i) => (
          <ShotFrame
            key={shot.src}
            shot={shot}
            onOpen={() => setLightbox(i + 1)}
          />
        ))}
      </Box>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography
            sx={{
              letterSpacing: "0.18em",
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "rgba(160,200,255,0.7)",
              mb: 1,
            }}
          >
            CAPABILITIES
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
            {project.capabilities.map((c) => (
              <Typography
                component="li"
                key={c}
                sx={{ color: "rgba(210,225,255,0.82)", fontSize: "0.9rem" }}
              >
                {c}
              </Typography>
            ))}
          </Stack>
        </Box>
        <Typography sx={{ color: "rgba(170,195,230,0.65)", fontSize: "0.78rem" }}>
          {project.tech.join(" · ")}
        </Typography>
      </Stack>

      {project.href ? (
        <Button
          component={Link}
          href={project.href}
          target="_blank"
          rel="noreferrer"
          variant="contained"
          sx={{
            px: 2.5,
            py: 1,
            borderRadius: 999,
            letterSpacing: "0.14em",
            fontSize: "0.72rem",
            fontWeight: 700,
            bgcolor: "rgba(0,180,255,0.9)",
            color: "#041018",
            textDecoration: "none",
            "&:hover": { bgcolor: "rgba(120,220,255,1)" },
          }}
        >
          {project.hrefLabel ?? "OPEN PROJECT"}
        </Button>
      ) : (
        <Typography
          sx={{
            letterSpacing: "0.14em",
            fontSize: "0.68rem",
            color: "rgba(160,190,230,0.55)",
          }}
        >
          {project.hrefLabel ?? "PRIVATE / LOCAL PRODUCT"}
        </Typography>
      )}

      {lightbox !== null ? (
        <Lightbox
          shots={all}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() =>
            setLightbox((i) => (i === null ? 0 : (i + all.length - 1) % all.length))
          }
          onNext={() => setLightbox((i) => (i === null ? 0 : (i + 1) % all.length))}
        />
      ) : null}
    </Box>
  )
}
